<template>
  <div class="w-full h-screen overflow-hidden bg-slate-100">
    <Topbar />

    <main class="dashboard-main flex h-[calc(100vh-64px)] flex-col overflow-hidden px-4 pb-5 pt-[84px] md:px-5 lg:px-6">
      <section class="dashboard-header mb-4 flex shrink-0 items-start justify-between gap-3">
        <div>
          <h1 class="dashboard-title text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Панель флориста</h1>
          <p class="mt-1 text-sm text-slate-500 md:text-base">
            Добро пожаловать, {{ employeeName }}
          </p>
        </div>
        <button
          type="button"
          class="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
          @click="fetchDashboardData"
        >
          Обновить
        </button>
      </section>

      <section class="dashboard-summary mb-4 grid shrink-0 grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <article class="dashboard-stat rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div class="mb-2 flex items-center justify-between">
            <i class="pi pi-dollar text-lg text-emerald-700"></i>
            <span class="text-lg text-emerald-600 font-semibold">{{ salesDeltaLabel }}</span>
          </div>
          <p class="dashboard-stat-value text-[30px] font-semibold leading-none text-slate-900 md:text-[34px]">{{ todaySalesAmount.toLocaleString('ru-RU') }} ₸</p>
          <p class="mt-2 text-sm text-slate-500 md:text-base">Продажи сегодня</p>
        </article>

        <article class="dashboard-stat rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div class="mb-2 flex items-center justify-between">
            <i class="pi pi-shopping-cart text-lg text-emerald-700"></i>
            <span class="text-lg text-emerald-600 font-semibold">+{{ todayChecksCount }}</span>
          </div>
          <p class="dashboard-stat-value text-[30px] font-semibold leading-none text-slate-900 md:text-[34px]">{{ todayChecksCount }}</p>
          <p class="mt-2 text-sm text-slate-500 md:text-base">Чеков</p>
        </article>

        <article class="dashboard-stat rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div class="mb-2 flex items-center justify-between">
            <i class="pi pi-chart-line text-lg text-emerald-700"></i>
          </div>
          <p class="dashboard-stat-value text-[30px] font-semibold leading-none text-slate-900 md:text-[34px]">{{ averageCheckAmount.toLocaleString('ru-RU') }} ₸</p>
          <p class="mt-2 text-sm text-slate-500 md:text-base">Ср. чек</p>
        </article>

        <article class="dashboard-stat rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div class="mb-2 flex items-center justify-between">
            <i class="pi pi-users text-lg text-emerald-700"></i>
          </div>
          <p class="dashboard-stat-value text-[30px] font-semibold leading-none text-slate-900 md:text-[34px]">{{ todayClientsCount }}</p>
          <p class="mt-2 text-sm text-slate-500 md:text-base">Клиентов сегодня</p>
        </article>
      </section>

      <section class="dashboard-panels grid min-h-0 flex-1 grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-5">
        <article class="dashboard-panel flex min-h-0 flex-col rounded-2xl border border-emerald-200 bg-white p-4 shadow-sm">
          <div class="mb-4 flex items-center justify-between gap-3">
            <h2 class="dashboard-panel-title text-xl font-semibold text-slate-900 md:text-2xl">Напоминания на сегодня</h2>
            <Button
              label="Все"
              icon="pi pi-arrow-right"
              severity="secondary"
              size="small"
              outlined
              @click="openClientRemindersPage"
            />
          </div>

          <div v-if="loading" class="py-8 text-base text-slate-500">Загрузка...</div>
          <div v-else-if="!todayReminders.length" class="py-8 text-base text-slate-500">На сегодня напоминаний нет</div>

          <div v-else class="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
            <div
              v-for="reminder in todayReminders"
              :key="`reminder-${reminder.id}`"
              class="dashboard-list-item rounded-2xl border border-emerald-100 bg-emerald-50 px-3 py-2.5"
            >
              <p class="truncate text-sm font-semibold text-slate-900 md:text-base">{{ reminder.title }}</p>
              <p class="truncate text-xs text-slate-500 md:text-sm">{{ reminder.client_name || 'Без клиента' }}</p>
              <div class="mt-2 flex items-center justify-between gap-2 text-xs md:text-sm">
                <span class="text-slate-500">{{ formatTime(reminder.due_at) || 'Сегодня' }}</span>
                <span class="font-semibold text-emerald-700">{{ getReminderTypeLabel(reminder.type) }}</span>
              </div>
            </div>
          </div>
        </article>

        <article class="dashboard-panel flex min-h-0 flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div class="mb-3 flex items-center justify-between gap-3">
            <h2 class="dashboard-panel-title text-xl font-semibold text-slate-900 md:text-2xl">Последние продажи</h2>
            <Button
              label="Подробнее"
              icon="pi pi-arrow-right"
              severity="secondary"
              size="small"
              outlined
              @click="openFloristSalesPage"
            />
          </div>

          <div v-if="loading" class="py-8 text-base text-slate-500">Загрузка...</div>
          <div v-else-if="!recentSales.length" class="py-8 text-base text-slate-500">Продаж пока нет</div>

          <div v-else class="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
            <div
              v-for="sale in recentSales"
              :key="sale.id"
              class="dashboard-list-item flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5"
            >
              <div class="min-w-0">
                <p class="truncate text-base font-medium text-slate-900 md:text-lg">{{ sale.sale_id }}</p>
                <p class="truncate text-xs text-slate-500 md:text-sm">
                  {{ sale.client || 'Без клиента' }} · {{ formatPhone(sale.client_phone) || 'Телефон не указан' }}
                </p>
                <p class="truncate text-xs text-slate-400 md:text-sm">{{ sale.account || 'Счёт' }}</p>
              </div>
              <div class="text-right shrink-0 ml-3">
                <p class="text-lg font-semibold text-slate-900 md:text-xl">{{ Number(sale.price_after_discount || 0).toLocaleString('ru-RU') }} ₸</p>
                <p class="text-xs text-slate-500 md:text-sm">{{ formatTime(sale.date) }}</p>
                <button
                  type="button"
                  class="mt-1 text-xs font-medium text-emerald-700 hover:text-emerald-800 cursor-pointer md:text-sm"
                  @click="openSaleDetails(sale)"
                >
                  Подробнее
                </button>
              </div>
            </div>
          </div>
        </article>

        <article class="dashboard-panel flex min-h-0 flex-col rounded-2xl border border-amber-200 bg-white p-4 shadow-sm">
          <div class="mb-4 flex items-center justify-between">
            <h2 class="dashboard-panel-title text-xl font-semibold text-slate-900 md:text-2xl">Низкий остаток</h2>
            <span class="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">≤ {{ lowStockThreshold }} шт</span>
          </div>

          <div v-if="loading" class="py-8 text-base text-slate-500">Загрузка...</div>
          <div v-else-if="!lowStockProducts.length" class="py-8 text-base text-slate-500">Товаров с низким остатком нет</div>

          <div v-else class="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
            <div
              v-for="item in lowStockProducts"
              :key="`low-${item.id}`"
              class="dashboard-list-item rounded-2xl border border-amber-100 bg-amber-50 px-3 py-2.5"
            >
              <p class="truncate text-sm font-semibold text-slate-900 md:text-base">{{ item.name }}</p>
              <p class="truncate text-xs text-slate-500 md:text-sm">{{ formatStoreSummaryBarcodes(item) }}</p>
              <div class="mt-2 flex items-center justify-between text-xs md:text-sm">
                <span class="text-slate-500">Остаток</span>
                <span class="font-semibold text-amber-700">{{ Number(item.total_quantity || 0).toLocaleString('ru-RU') }}</span>
              </div>
            </div>
          </div>
        </article>

        <article class="dashboard-panel flex min-h-0 flex-col rounded-2xl border border-rose-200 bg-white p-4 shadow-sm">
          <div class="mb-4 flex items-center justify-between">
            <h2 class="dashboard-panel-title text-xl font-semibold text-slate-900 md:text-2xl">Срок годности истекает</h2>
            <span class="rounded-full bg-rose-100 px-2.5 py-1 text-xs font-semibold text-rose-700">{{ expirySoonMonths }} месяца</span>
          </div>

          <div v-if="loading" class="py-8 text-base text-slate-500">Загрузка...</div>
          <div v-else-if="!expiringSoonItems.length" class="py-8 text-base text-slate-500">Просрочки и ближайших сроков нет</div>

          <div v-else class="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
            <div
              v-for="item in expiringSoonItems"
              :key="`exp-${item.store_id}`"
              class="dashboard-list-item rounded-2xl border border-rose-100 bg-rose-50 px-3 py-2.5"
            >
              <p class="truncate text-sm font-semibold text-slate-900 md:text-base">{{ item.name }}</p>
              <p class="truncate text-xs text-slate-500 md:text-sm">{{ item.barcode }}</p>
              <div class="mt-2 flex items-center justify-between text-xs md:text-sm">
                <span class="text-slate-500">{{ formatShortDate(item.date) }}</span>
                <span class="font-semibold" :class="item.daysLeft < 0 ? 'text-rose-700' : 'text-rose-600'">
                  {{ item.daysLeft < 0 ? `Просрочен на ${Math.abs(item.daysLeft)} дн.` : `Осталось ${item.daysLeft} дн.` }}
                </span>
              </div>
            </div>
          </div>
        </article>

        <article class="dashboard-panel flex min-h-0 flex-col rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
          <div class="mb-4 flex items-center justify-between">
            <h2 class="dashboard-panel-title text-xl font-semibold text-slate-900 md:text-2xl">Нет в наличии</h2>
            <span class="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{{ outOfStockProducts.length }}</span>
          </div>

          <div v-if="loading" class="py-8 text-base text-slate-500">Загрузка...</div>
          <div v-else-if="!outOfStockProducts.length" class="py-8 text-base text-slate-500">Все товары в наличии</div>

          <div v-else class="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
            <div
              v-for="item in outOfStockProducts"
              :key="`out-${item.id}`"
              class="dashboard-list-item rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5"
            >
              <p class="truncate text-sm font-semibold text-slate-900 md:text-base">{{ item.name }}</p>
              <p class="truncate text-xs text-slate-500 md:text-sm">{{ formatStoreSummaryBarcodes(item) }}</p>
              <div class="mt-2 flex items-center justify-between text-xs md:text-sm">
                <span class="text-slate-500">Остаток</span>
                <span class="font-semibold text-slate-700">0</span>
              </div>
            </div>
          </div>
        </article>
      </section>
    </main>
  </div>

  <Dialog
    v-model:visible="showSaleDetailsDialog"
    modal
    header="Детали продажи"
    :style="{ width: '1040px', maxWidth: '96vw' }"
  >
    <div v-if="saleDetailsLoading" class="text-gray-500 text-base py-6">Загрузка деталей...</div>
    <div v-else-if="!selectedSaleDetails" class="text-gray-500 text-base py-6">Данные по продаже не найдены</div>

    <div v-else class="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
      <section class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <h3 class="mb-3 text-lg font-semibold text-slate-900">Продажа</h3>
        <div class="space-y-3 text-sm">
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Номер</p>
            <p class="mt-1 font-semibold text-slate-900">{{ selectedSaleDetails.sale_id }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Дата</p>
            <p class="mt-1 font-semibold text-slate-900">{{ formatDateTime(selectedSaleDetails.date) }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Клиент</p>
            <p class="mt-1 font-semibold text-slate-900">{{ selectedSaleDetails.client?.name || 'Без клиента' }}</p>
            <p class="mt-1 text-slate-500">{{ formatPhone(selectedSaleDetails.client?.phone) || 'Телефон не указан' }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Продавец</p>
            <p class="mt-1 font-semibold text-slate-900">{{ selectedSaleDetails.employee?.name || '—' }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Счёт</p>
            <p class="mt-1 font-semibold text-slate-900">{{ selectedSaleDetails.bank }} · {{ selectedSaleDetails.account }}</p>
          </div>
        </div>
      </section>

      <section class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div class="mx-auto w-full max-w-3xl rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-4 md:p-5">
          <div class="border-b border-dashed border-slate-300 pb-4 text-center">
            <p class="text-xs font-medium uppercase tracking-[0.22em] text-slate-500">Чек продажи</p>
            <p class="mt-2 text-2xl font-semibold text-slate-900">{{ selectedSaleDetails.sale_id }}</p>
            <p class="mt-1 text-sm text-slate-500">{{ formatDateTime(selectedSaleDetails.date) }}</p>
          </div>

          <div class="mt-4 space-y-2 border-b border-dashed border-slate-300 pb-4 text-sm text-slate-600">
            <div class="flex items-start justify-between gap-3">
              <span>Клиент</span>
              <div class="text-right">
                <p class="font-medium text-slate-900">{{ selectedSaleDetails.client?.name || 'Без клиента' }}</p>
                <p class="text-xs text-slate-500">{{ formatPhone(selectedSaleDetails.client?.phone) || 'Телефон не указан' }}</p>
              </div>
            </div>
            <div class="flex items-start justify-between gap-3">
              <span>Счёт</span>
              <span class="text-right font-medium text-slate-900">{{ selectedSaleDetails.bank }} · {{ selectedSaleDetails.account }}</span>
            </div>
            <div class="flex items-start justify-between gap-3">
              <span>Продавец</span>
              <span class="text-right font-medium text-slate-900">{{ selectedSaleDetails.employee?.name || '—' }}</span>
            </div>
          </div>

          <div class="mt-4 space-y-3">
            <article
              v-for="item in saleReceiptItems"
              :key="item.id"
              class="rounded-2xl border border-slate-200 bg-slate-50 p-3"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="truncate text-base font-semibold text-slate-900">{{ item.label }}</p>
                  <p class="mt-1 text-xs text-slate-500">{{ item.meta }}</p>
                </div>
                <div class="text-right shrink-0">
                  <p class="text-base font-semibold text-slate-900">{{ formatCurrency(item.total_price) }}</p>
                  <p class="mt-1 text-xs text-slate-500">{{ item.quantityLabel }}</p>
                </div>
              </div>

              <!-- <div v-if="item.components.length" class="mt-3 space-y-2 rounded-xl border border-slate-200 bg-white p-3">
                <div
                  v-for="component in item.components"
                  :key="`${item.id}-${component.product_id}-${component.barcode}`"
                  class="flex items-start justify-between gap-3 text-sm"
                >
                  <div class="min-w-0">
                    <p class="truncate font-medium text-slate-900">{{ component.name }}</p>
                    <p class="truncate text-xs text-slate-500">{{ component.barcode || 'Штрихкод не указан' }}</p>
                  </div>
                  <div class="shrink-0 text-right text-xs text-slate-600">
                    <p>{{ Number(component.quantity || 0) }} × {{ formatCurrency(component.unit_price) }}</p>
                    <p class="mt-1 font-semibold text-slate-900">{{ formatCurrency(component.total_price) }}</p>
                  </div>
                </div>
              </div> -->

              <!-- <div class="mt-3 space-y-1 rounded-xl bg-white px-3 py-2 text-sm">
                <div class="flex items-center justify-between gap-3 text-slate-600">
                  <span>Сумма компонентов</span>
                  <span>{{ formatCurrency(item.componentsTotal) }}</span>
                </div>
                <div v-if="item.extraCharge > 0" class="flex items-center justify-between gap-3 text-slate-600">
                  <span>{{ item.extraChargeLabel }}</span>
                  <span>{{ formatCurrency(item.extraCharge) }}</span>
                </div>
                <div class="flex items-center justify-between gap-3 border-t border-dashed border-slate-200 pt-2 font-semibold text-slate-900">
                  <span>Итого по позиции</span>
                  <span>{{ formatCurrency(item.total_price) }}</span>
                </div>
              </div> -->

              <div v-if="item.showComposition" class="mt-3">
                <button
                  type="button"
                  class="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                  @click="toggleSaleItemDetails(item.id)"
                >
                  <span class="font-medium">
                    Состав позиции
                    <span class="text-slate-500">({{ item.components.length }})</span>
                  </span>
                  <i
                    class="pi text-xs text-slate-500"
                    :class="isSaleItemExpanded(item.id) ? 'pi-chevron-up' : 'pi-chevron-down'"
                  />
                </button>

                <div
                  v-if="isSaleItemExpanded(item.id)"
                  class="mt-2 space-y-2 rounded-xl border border-slate-200 bg-white p-3"
                >
                  <div
                    v-for="component in item.components"
                    :key="`${item.id}-${component.product_id}-${component.barcode}`"
                    class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5"
                  >
                    <div class="flex items-start justify-between gap-3 text-sm">
                      <div class="min-w-0">
                        <p class="truncate font-medium text-slate-900">{{ component.name }}</p>
                        <p class="truncate text-xs text-slate-500">{{ component.barcode || 'Штрихкод не указан' }}</p>
                        <p class="mt-1 truncate text-xs text-slate-400">
                          {{ component.classification || 'Без класса' }} · {{ component.category || 'Без категории' }}
                        </p>
                      </div>
                      <div class="shrink-0 text-right text-xs text-slate-600">
                        <p>{{ Number(component.quantity || 0) }} × {{ formatCurrency(component.unit_price) }}</p>
                        <p class="mt-1 font-semibold text-slate-900">{{ formatCurrency(component.total_price) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>

          <div class="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
            <div class="flex items-center justify-between gap-3 text-sm text-slate-700">
              <span>Сумма по чеку</span>
              <span class="font-medium text-slate-900">{{ formatCurrency(selectedSaleDetails.total_amount) }}</span>
            </div>
            <div v-if="saleDiscountAmount > 0" class="mt-2 flex items-center justify-between gap-3 text-sm text-slate-700">
              <span>Скидка / бонусы</span>
              <span class="font-medium text-emerald-700">-{{ formatCurrency(saleDiscountAmount) }}</span>
            </div>
            <div class="mt-3 flex items-center justify-between gap-3 border-t border-dashed border-emerald-200 pt-3">
              <span class="text-base font-semibold text-slate-900">К оплате</span>
              <span class="text-2xl font-semibold text-slate-900">{{ formatCurrency(selectedSaleDetails.price_after_discount) }}</span>
            </div>
          </div>
        </div>
      </section>
    </div>

    <template #footer>
      <Button label="Закрыть" severity="secondary" outlined @click="showSaleDetailsDialog = false" />
    </template>
  </Dialog>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import axios from '../lib/axios'
import Topbar from './Topbar.vue'

const router = useRouter()
const loading = ref(false)
const sales = ref([])
const reminders = ref([])
const storeSummary = ref([])
const storeRows = ref([])
const employee = ref(null)
const saleDetailsLoading = ref(false)
const selectedSaleDetails = ref(null)
const showSaleDetailsDialog = ref(false)
const expandedSaleItemIds = ref([])
const almatyTimeZone = 'Asia/Almaty'
const lowStockThreshold = 5
const expirySoonMonths = 3
const SINGLE_PRODUCT_ORDER_TYPE_ID = 8
const EXTRA_WORK_ORDER_TYPE_ID = 9

const employeeName = computed(() => {
  return employee.value?.name || 'флорист'
})

const getAlmatyDateParts = (value = new Date()) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: almatyTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  const parts = formatter.formatToParts(value)
  const map = Object.fromEntries(parts.map(part => [part.type, part.value]))

  return {
    year: map.year,
    month: map.month,
    day: map.day,
    hour: map.hour,
    minute: map.minute,
  }
}

const todayIsoDate = computed(() => {
  const { year, month, day } = getAlmatyDateParts()
  return `${year}-${month}-${day}`
})

const todaySales = computed(() => {
  return (sales.value || []).filter((sale) => String(sale.date || '').startsWith(todayIsoDate.value))
})

const todaySalesAmount = computed(() => {
  return todaySales.value.reduce((sum, sale) => sum + Number(sale.price_after_discount || 0), 0)
})

const todayChecksCount = computed(() => todaySales.value.length)

const todayClientsCount = computed(() => {
  const clients = new Set(
    todaySales.value
      .map((sale) => sale.client)
      .filter((client) => typeof client === 'string' && client.trim().length > 0)
  )

  return clients.size
})

const averageCheckAmount = computed(() => {
  const checksCount = Number(todayChecksCount.value || 0)
  if (checksCount <= 0) return 0

  return Math.round(Number(todaySalesAmount.value || 0) / checksCount)
})

const recentSales = computed(() => {
  return [...todaySales.value]
    .sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime())
})

const todayReminders = computed(() => {
  return [...(reminders.value || [])]
    .sort((a, b) => new Date(a.due_at || 0).getTime() - new Date(b.due_at || 0).getTime())
})

const lowStockProducts = computed(() => {
  return [...(storeSummary.value || [])]
    .filter(item => Number(item.total_quantity || 0) > 0 && Number(item.total_quantity || 0) <= lowStockThreshold)
    .sort((a, b) => Number(a.total_quantity || 0) - Number(b.total_quantity || 0))
})

const outOfStockProducts = computed(() => {
  return [...(storeSummary.value || [])]
    .filter(item => Number(item.total_quantity || 0) <= 0)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ru'))
})

const expiringSoonItems = computed(() => {
  const today = new Date(`${todayIsoDate.value}T00:00:00`)
  const warningDate = new Date(today)
  warningDate.setMonth(warningDate.getMonth() + expirySoonMonths)
  warningDate.setHours(0, 0, 0, 0)

  return [...(storeRows.value || [])]
    .map((item) => {
      const rawDate = String(item.date || '')
      const date = rawDate ? new Date(`${rawDate}T00:00:00`) : null
      if (!date || Number.isNaN(date.getTime())) {
        return null
      }

      const diffMs = date.getTime() - today.getTime()
      const daysLeft = Math.floor(diffMs / 86400000)

      return {
        ...item,
        daysLeft,
      }
    })
    .filter(item => item && Number(item.amount || 0) > 0 && new Date(`${item.date}T00:00:00`) <= warningDate)
    .sort((a, b) => a.daysLeft - b.daysLeft)
})

const salesDeltaLabel = computed(() => {
  return todaySalesAmount.value > 0 ? '+12%' : '+0%'
})

const getReminderTypeLabel = (type) => ({
  call: 'Звонок',
  message: 'Сообщение',
  birthday: 'День рождения',
  follow_up: 'Повторный контакт',
  custom: 'Другое',
}[type] || 'Другое')

const saleReceiptItems = computed(() => {
  const items = selectedSaleDetails.value?.items || []

  return items.map((item, index) => {
    const components = Array.isArray(item.components) ? item.components : []
    const isSingleProduct = Number(item.order_type_id || 0) === SINGLE_PRODUCT_ORDER_TYPE_ID
    const componentsTotal = components.reduce((sum, component) => sum + Number(component.total_price || 0), 0)
    const totalPrice = Number(item.total_price || 0)
    const extraCharge = Math.max(0, totalPrice - componentsTotal)
    const componentQuantity = components.reduce((sum, component) => sum + Number(component.quantity || 0), 0)
    const displayQuantity = isSingleProduct && componentQuantity > 0
      ? componentQuantity
      : Number(item.quantity || 0) || 1
    const primaryName = item.title
      ? String(item.title).trim()
      : Number(item.order_type_id || 0) === EXTRA_WORK_ORDER_TYPE_ID
        ? 'Доп работа'
        : components.length === 1
        ? components[0]?.name
        : item.product_type || null

    return {
      ...item,
      label: primaryName || `Позиция ${index + 1}`,
      showComposition: !isSingleProduct && components.length > 0,
      meta: [
        item.product_type,
        item.product_level,
        components.length > 1 ? `${components.length} компонентов` : null,
      ].filter(Boolean).join(' · '),
      quantityLabel: `Кол-во: ${displayQuantity}`,
      components,
      componentsTotal,
      extraCharge,
      extraChargeLabel: components.length ? 'Сборка / наценка' : 'Доп. работа',
    }
  })
})

const saleDiscountAmount = computed(() => {
  const total = Number(selectedSaleDetails.value?.total_amount || 0)
  const payable = Number(selectedSaleDetails.value?.price_after_discount || 0)

  return Math.max(0, total - payable)
})

const formatTime = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const { hour: hh, minute: mm } = getAlmatyDateParts(date)
  return `${hh}:${mm}`
}

const formatDateTime = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  const { year: y, month: m, day: d, hour: hh, minute: mm } = getAlmatyDateParts(date)
  return `${y}-${m}-${d} ${hh}:${mm}`
}

const formatShortDate = (value) => {
  if (!value) return ''
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return String(value)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

const formatPhone = (value) => {
  const digits = String(value ?? '').replace(/\D+/g, '')
  if (!digits) return ''

  const normalized = digits.length === 10 ? `7${digits}` : digits
  if (normalized.length !== 11 || !normalized.startsWith('7')) {
    return String(value ?? '')
  }

  return `+${normalized[0]}(${normalized.slice(1, 4)})${normalized.slice(4, 7)}-${normalized.slice(7, 9)}-${normalized.slice(9, 11)}`
}

const formatCurrency = (value) => {
  return `${Number(value || 0).toLocaleString('ru-RU')} ₸`
}

const formatStoreSummaryBarcodes = (item) => {
  const barcodes = Array.isArray(item?.barcodes)
    ? item.barcodes
        .map((barcodeItem) => String(barcodeItem?.barcode || '').trim())
        .filter(Boolean)
    : []

  return barcodes.length ? barcodes.join(', ') : 'Штрихкод не указан'
}

const isSaleItemExpanded = (itemId) => expandedSaleItemIds.value.includes(itemId)

const toggleSaleItemDetails = (itemId) => {
  if (isSaleItemExpanded(itemId)) {
    expandedSaleItemIds.value = expandedSaleItemIds.value.filter((id) => id !== itemId)
    return
  }

  expandedSaleItemIds.value = [...expandedSaleItemIds.value, itemId]
}

const openSaleDetails = async (sale) => {
  if (!sale?.id) return

  showSaleDetailsDialog.value = true
  saleDetailsLoading.value = true
  expandedSaleItemIds.value = []
  try {
    const res = await axios.get(`/api/sale-items/${sale.id}`)
    selectedSaleDetails.value = res.data || null
    expandedSaleItemIds.value = (selectedSaleDetails.value?.items || [])
      .filter((item) => Number(item.order_type_id || 0) !== 8 && Array.isArray(item.components) && item.components.length > 0)
      .slice(0, 1)
      .map((item) => item.id)
  } catch (error) {
    selectedSaleDetails.value = null
    expandedSaleItemIds.value = []
  } finally {
    saleDetailsLoading.value = false
  }
}

const openFloristSalesPage = () => {
  router.push('/dashboard-florist')
}

const openClientRemindersPage = () => {
  router.push('/client-reminders')
}

const fetchDashboardData = async () => {
  loading.value = true
  try {
    const barcode = localStorage.getItem('barcode')

    const [salesRes, employeeRes, storeSummaryRes, storeRowsRes, remindersRes] = await Promise.all([
      axios.get('/api/sale').catch(() => ({ data: [] })),
      axios.get('/api/get-employee', { params: { barcode } }).catch(() => ({ data: null })),
      axios.get('/api/general-store', { params: { storage: 0 } }).catch(() => ({ data: [] })),
      axios.get('/api/store', { params: { storage: 0 } }).catch(() => ({ data: [] })),
      axios.get('/api/client-reminders', { params: { scope: 'today', limit: 20 } }).catch(() => ({ data: [] }))
    ])

    sales.value = Array.isArray(salesRes.data) ? salesRes.data : []
    reminders.value = Array.isArray(remindersRes.data) ? remindersRes.data : []
    employee.value = employeeRes.data || null
    storeSummary.value = Array.isArray(storeSummaryRes.data) ? storeSummaryRes.data : Object.values(storeSummaryRes.data || {})
    storeRows.value = Array.isArray(storeRowsRes.data) ? storeRowsRes.data : []
  } finally {
    loading.value = false
  }
}

onMounted(fetchDashboardData)
</script>

<style scoped>
@media (max-width: 1680px) and (max-height: 900px) {
  .dashboard-main {
    padding-top: 76px;
    padding-bottom: 16px;
  }

  .dashboard-header {
    margin-bottom: 12px;
  }

  .dashboard-title {
    font-size: 1.75rem;
    line-height: 1.1;
  }

  .dashboard-summary,
  .dashboard-panels {
    gap: 12px;
  }

  .dashboard-stat,
  .dashboard-panel {
    padding: 14px;
    border-radius: 18px;
  }

  .dashboard-stat-value {
    font-size: 1.7rem !important;
  }

  .dashboard-panel-title {
    font-size: 1rem !important;
    line-height: 1.2 !important;
  }

  .dashboard-list-item {
    padding: 10px 12px;
    border-radius: 16px;
  }
}

@media (max-width: 1180px) {
  h1 {
    font-size: 2rem;
    line-height: 1.1;
  }

  h2 {
    font-size: 1.75rem;
    line-height: 1.2;
  }

  .text-5xl {
    font-size: 2rem !important;
    line-height: 1.15 !important;
  }

  .text-4xl {
    font-size: 1.5rem !important;
    line-height: 1.2 !important;
  }

  .text-3xl {
    font-size: 1.25rem !important;
    line-height: 1.2 !important;
  }

  .text-2xl {
    font-size: 0.95rem !important;
    line-height: 1.3 !important;
  }
}
</style>
